import { useEffect, useState } from 'react';
import { ConfigProvider, Layout, Tabs, Button, Typography, Select, Space, theme, Spin } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import keycloak from './keycloak';
import deDE from 'antd/locale/de_DE';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import InvoiceLogo from './InvoiceLogo';
import InvoicesTab from './components/InvoicesTab';
import CommissionModelsTab from './components/CommissionModelsTab';
import { api } from './api';
import { useUrlParam } from './hooks/useUrlParam';
import './App.css';

dayjs.locale('de');

const { Header, Content } = Layout;
const { Title } = Typography;

export default function App() {
  const roles = keycloak.resourceAccess?.['fair-invoices-app']?.roles || [];
  const isBuchhalter = roles.includes('buchhalter');
  const isManager = roles.includes('manager');

  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useUrlParam('store');
  const [activeTab, setActiveTab] = useUrlParam('tab', 'invoices');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.stores.list().then((storeList) => {
      setStores(storeList);
      const urlStore = new URLSearchParams(window.location.search).get('store');
      if (!urlStore && storeList.length === 1) {
        setSelectedStore(storeList[0].id);
      } else if (urlStore && !storeList.some(s => s.id === urlStore)) {
        setSelectedStore(null);
      }
      setLoading(false);
    });
  }, [setSelectedStore]);

  const tabs = selectedStore
    ? [
        { key: 'invoices', label: 'Rechnungen', children: <InvoicesTab storeId={selectedStore} /> },
        ...(isBuchhalter ? [
          { key: 'commissions', label: 'Provisionsmodelle', children: <CommissionModelsTab storeId={selectedStore} /> },
        ] : []),
      ]
    : [];

  return (
    <ConfigProvider
      locale={deDE}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary:        '#2f5d8a',
          colorLink:           '#2f5d8a',
          colorBgLayout:       '#eef2f5',
          colorBgContainer:    '#ffffff',
          colorBorder:         '#c9d9e8',
          colorTextSecondary:  '#3d5872',
          borderRadius:        6,
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', background: '#eef2f5' }}>
        <Header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', background: '#1f3b57',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <InvoiceLogo size={32} />
            <Title level={4} style={{ color: '#fff', margin: 0, lineHeight: 1 }}>Fair Invoices</Title>
          </div>
          <Space>
            <Select
              placeholder="Store wählen…"
              style={{ width: 220, background: '#fff', borderRadius: 6 }}
              value={selectedStore}
              onChange={setSelectedStore}
              options={stores.map(s => ({ value: s.id, label: s.name }))}
              variant="outlined"
              showSearch
              optionFilterProp="label"
            />
            <Button ghost icon={<LogoutOutlined />} onClick={() => keycloak.logout()}
              style={{ borderColor: '#fff', color: '#fff' }}>
              Abmelden
            </Button>
          </Space>
        </Header>
        <Content style={{ padding: '24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
          ) : !selectedStore ? (
            <div style={{ textAlign: 'center', padding: 80, color: '#3d5872' }}>
              {stores.length === 0
                ? (isManager && !isBuchhalter
                    ? 'Ihrem Konto ist kein Store zugeordnet.'
                    : 'Keine Stores gefunden.')
                : 'Bitte einen Store auswählen.'}
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabs}
              size="large"
              style={{ background: '#ffffff', borderRadius: 8, padding: '0 16px 16px',
                       border: '1px solid #c9d9e8' }}
            />
          )}
        </Content>
      </Layout>
    </ConfigProvider>
  );
}
